/* === Sage Custom Code Start (Apr 2026) === */
/* Added js to stop main product image flickers on iphone (line 45-85) */


import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {




  connectedCallback() {
  super.connectedCallback();

  const { signal } = this.#controller;
  const target = this.closest('.shopify-section, dialog');

  target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

  this.refs.zoomDialogComponent?.addEventListener(
    ThemeEvents.zoomMediaSelected,
    this.#handleZoomMediaSelected,
    { signal }
  );

  // ========================================
  // INSTANT SCROLL TO TOP WHEN COLOR SWATCH
  // IS CLICKED
  // ========================================

  document.addEventListener(
    'click',
    this.#handleColorSwatchClick,
    {
      signal,
      capture: true
    }
  );
}





  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }



  // ========================================
  // INSTANT COLOR SWATCH SCROLL
  // ========================================

  #handleColorSwatchClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;




    const colorSwatch = target.closest(
  '.variant-option--swatches input[type="radio"], .variant-option--swatches label'
);

if (!colorSwatch) return;


/*
 * Do NOT scroll when the swatch belongs to a
 * product card, collection card, quick add, etc.
 */
if (
  colorSwatch.closest('product-card') ||
  colorSwatch.closest('.product-card') ||
  colorSwatch.closest('quick-add-dialog') ||
  colorSwatch.closest('quick-add-component')
) {
  return;
}


/*
 * Only the actual product-page color swatch
 * should scroll the page to the top.
 */
window.scrollTo({
  top: 0,
  left: 0,
  behavior: 'auto'
});



  };




  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */

  #handleVariantUpdate = (event) => {
  const source = event.detail.data.html;
  if (!source) return;

  const newMediaGallery = source.querySelector('media-gallery');
  if (!newMediaGallery) return;

  const parent = this.parentNode;
  if (!parent) return;

  // PRELOAD images without inserting into DOM
  const images = newMediaGallery.querySelectorAll('img');
  let loaded = 0;

  const done = () => {
    loaded++;
    if (loaded >= images.length) {
      requestAnimationFrame(() => {
        parent.replaceChild(newMediaGallery, this);
      });
    }
  };

  if (images.length === 0) {
    done();
    return;
  }

  images.forEach((img) => {
    // Force eager decoding (important for Safari)
    img.loading = 'eager';
    img.decoding = 'sync';

    if (img.complete) {
      done();
    } else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  });
};

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
